import React, { useEffect, useState } from 'react';
import { signInAnonymously, signOut } from 'firebase/auth';
import { collection, doc, getDocs, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import {
  ArrowRight,
  Award,
  BookOpen,
  Briefcase,
  Camera,
  CheckCircle2,
  ClipboardList,
  FileText,
  Lightbulb,
  Lock,
  LogOut,
  MapPin,
  Menu,
  Mic,
  Palette,
  PlayCircle,
  ShieldCheck,
  Sparkles,
  Target,
  UploadCloud,
  Users,
  X,
} from 'lucide-react';
import { auth, db, storage } from './firebase';
import {
  MEGA_PROJECT_ID,
  MEGA_PROJECT_TITLE,
  MEGA_PROJECT_SUBTITLE,
  MEGA_PROJECT_WINDOW,
  MEGA_ROLE_TAXONOMY,
  MEGA_CALENDAR,
  BOARD_MEMBER_ALIASES,
  PERIOD_CHAPTER_ASSIGNMENTS,
  PROGRESS_SECTIONS,
  getTodayCalendarRow,
  getNextCalendarRow,
} from './megaProject';

const PERIODS = ['1', '3', '4', '8', '9'];

const MODULES = [
  {
    id: 'mission',
    label: 'Mission brief',
    icon: Target,
    role: 'Everyone',
    status: 'Start here',
    description: 'Understand the class segment, career lane, team role, and final production target.',
  },
  {
    id: 'brainstorm',
    label: 'Career brainstorm',
    icon: Lightbulb,
    role: 'Researchers',
    status: 'Draft ideas',
    description: 'Collect career ideas and explain why each career belongs in The Living World.',
  },
  {
    id: 'research',
    label: 'Research packet',
    icon: BookOpen,
    role: 'Lead Researcher',
    status: 'Gather evidence',
    description: 'Build the science facts, vocabulary, examples, and source list the script will need.',
  },
  {
    id: 'script',
    label: 'Script studio',
    icon: FileText,
    role: 'Scriptwriter',
    status: 'Write narration',
    description: 'Turn the research into a clear student-facing documentary segment.',
  },
  {
    id: 'media',
    label: 'Presentation media',
    icon: Palette,
    role: 'Visual Designer',
    status: 'Prepare visuals',
    description: 'Plan visuals, upload evidence, and make the presentation recording-ready.',
  },
  {
    id: 'performance',
    label: 'On-screen practice',
    icon: Mic,
    role: 'On-Screen Talent',
    status: 'Rehearse',
    description: 'Practice delivery, timing, vocabulary, and transitions before recording day.',
  },
  {
    id: 'reflection',
    label: 'Reflection',
    icon: ClipboardList,
    role: 'Everyone',
    status: 'Close the loop',
    description: 'Evaluate your own work and your team collaboration after the project.',
  },
];

const SAMPLE_STUDENT = {
  id: 'sample-student',
  alias: 'Nova Lens',
  period: '1',
  assignedCareer: 'Cardiologist',
  projectId: MEGA_PROJECT_ID,
  claimed: true,
  podLeader: true,
  boardMember: true,
  careerLeader: true,
  careerLed: 'Cardiologist',
  onScreenAllowed: true,
  roles: ['Lead Researcher', 'Producer / Continuity'],
};

const WORKSPACE_MODULES = MODULES.filter((module) => (
  ['brainstorm', 'research', 'script', 'media', 'reflection'].includes(module.id)
));

const SCRIPT_BEATS = [
  ['hook', 'Hook', 'Open with a question, problem, or vivid career moment.'],
  ['intro', 'Career intro', 'Name the career and what the audience should understand.'],
  ['scienceInAction', 'Science in action', 'Explain the chapter science through the career task.'],
  ['impact', 'Human impact', 'Show who benefits and why the work matters.'],
  ['closing', 'Closing line', 'End with a memorable final sentence.'],
];

const SCHOOL_LOCATIONS = [
  'Cafeteria',
  'Middle school playground',
  'IM school playground',
  'Hill of the playground',
  'Soccer field',
  'Gym',
  'Amphitheater',
  'Rainbow building',
  'Custom',
];

const SECTION_TIPS = {
  brainstorm: [
    'Pick careers that make the science visible on camera.',
    'Use pros and cons to decide what the audience can understand quickly.',
    'Your pitch should explain why this career belongs in The Living World.',
  ],
  research: [
    'Start with what the career actually does before adding facts.',
    'Use at least two sources you would trust if your name was on the video.',
    'Turn vocabulary into usable script language, not dictionary definitions.',
  ],
  script: [
    'Write for speaking: short sentences, clear transitions, and one idea at a time.',
    'Every beat now needs a shoot location plan so recording day is not improvised.',
    'The script should tell the camera what the audience should see.',
  ],
  media: [
    'Upload the slide deck or visual PDF first, then the recording file when ready.',
    'Use export notes to tell the teacher what changed or what still needs attention.',
    'Files saved here are visible to the teacher dashboard preview.',
  ],
  reflection: [
    'Be specific about your contribution so grading is fair.',
    'Flag team issues only when the teacher needs to intervene.',
    'Close the loop by explaining what you learned and what you would improve.',
  ],
};

function formatProjectWindow(windowValue) {
  if (!windowValue || typeof windowValue !== 'object') return String(windowValue || 'Project window pending');
  const fmt = (value) => {
    if (!value) return 'TBD';
    const date = new Date(`${value}T12:00:00`);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  return `${fmt(windowValue.startDate)} to ${fmt(windowValue.endDate)}`;
}

function getLegacyPodDocId(student) {
  return `${student?.period || 'period'}_${student?.pod || student?.assignedCareer || student?.career || 'career'}`;
}

function getMegaPodDocId(student) {
  const career = `${student?.assignedCareer || student?.career || student?.pod || 'career'}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  return `${student?.projectId || MEGA_PROJECT_ID}_${student?.period || 'period'}_${career || 'career'}`;
}

function getDraftKey(student) {
  return `sa_lab_autosave_v2_${student?.id || student?.alias || 'preview'}`;
}

function slugifyId(value) {
  return `${value || 'student'}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '') || 'student';
}

function isAuthServiceBlocked(error) {
  const text = `${error?.code || ''} ${error?.message || ''}`.toLowerCase();
  return (
    text.includes('consumer') ||
    text.includes('suspended') ||
    text.includes('permission-denied') ||
    text.includes('auth/network-request-failed')
  );
}

function buildLocalStudent(period, alias, career) {
  const safePeriod = String(period || '1');
  const chapter = PERIOD_CHAPTER_ASSIGNMENTS[safePeriod] || {};
  const safeAlias = `${alias || `Period ${safePeriod} Ambassador`}`.trim();
  const safeCareer = `${career || chapter.careerLane || chapter.chapterTitle || 'Living World career'}`.trim();
  return {
    id: `local-${safePeriod}-${slugifyId(safeAlias)}`,
    alias: safeAlias,
    period: safePeriod,
    assignedCareer: safeCareer,
    projectId: MEGA_PROJECT_ID,
    claimed: true,
    localOnly: true,
    onScreenAllowed: true,
    roles: ['Lead Researcher', 'Scriptwriter'],
  };
}

function createLocationPlan() {
  return SCRIPT_BEATS.reduce((acc, [key]) => ({
    ...acc,
    [key]: { places: [], customPlace: '', justification: '' },
  }), {});
}

function createStudioDraft(student = {}) {
  const career = student.assignedCareer || student.career || 'your assigned career';
  return {
    brainstorm: {
      careers: [
        { idea: career, pros: 'Connects directly to the team assignment.', cons: 'Needs specific science examples.' },
        { idea: '', pros: '', cons: '' },
        { idea: '', pros: '', cons: '' },
      ],
      selectedCareer: career,
      pitch: '',
    },
    research: {
      jobDescription: '',
      responsibilities: ['', '', '', '', ''],
      education: '',
      salaryRange: { min: '', max: '' },
      coreConnection: '',
      realWorldExample: '',
      vocab: [
        { term: '', use: '' },
        { term: '', use: '' },
        { term: '', use: '' },
      ],
      sources: [
        { title: '', url: '' },
        { title: '', url: '' },
      ],
      surprisingFact: '',
    },
    script: {
      script: {
        hook: '',
        intro: '',
        scienceInAction: '',
        impact: '',
        closing: '',
      },
      storyboard: [
        { says: '', sees: '' },
        { says: '', sees: '' },
        { says: '', sees: '' },
        { says: '', sees: '' },
        { says: '', sees: '' },
      ],
      locationPlan: createLocationPlan(),
    },
    media: {
      slidePlan: '',
      visualEvidence: ['', '', ''],
      pdfReady: false,
      videoReady: false,
      exportNotes: '',
      fileName: '',
      fileUrl: '',
      videoFileName: '',
      videoFileUrl: '',
    },
    reflection: {
      selfScores: { research: 3, teamwork: 3, accuracy: 3 },
      contribution: '',
      challenge: '',
      teamIssues: false,
      teamExplanation: '',
    },
  };
}

function mergeStudioDraft(base, saved) {
  if (!saved) return base;
  const data = saved.data || saved;
  return {
    ...base,
    ...data,
    brainstorm: {
      ...base.brainstorm,
      ...(data.brainstorm || {}),
      careers: data.brainstorm?.careers || base.brainstorm.careers,
    },
    research: {
      ...base.research,
      ...(data.research || {}),
      salaryRange: { ...base.research.salaryRange, ...(data.research?.salaryRange || {}) },
      responsibilities: data.research?.responsibilities || base.research.responsibilities,
      vocab: data.research?.vocab || base.research.vocab,
      sources: data.research?.sources || base.research.sources,
    },
    script: {
      ...base.script,
      ...(data.script || {}),
      script: { ...base.script.script, ...(data.script?.script || {}) },
      storyboard: data.script?.storyboard || base.script.storyboard,
      locationPlan: { ...base.script.locationPlan, ...(data.script?.locationPlan || {}) },
    },
    media: {
      ...base.media,
      ...(data.media || {}),
      visualEvidence: data.media?.visualEvidence || base.media.visualEvidence,
    },
    reflection: {
      ...base.reflection,
      ...(data.reflection || {}),
      selfScores: { ...base.reflection.selfScores, ...(data.reflection?.selfScores || {}) },
    },
  };
}

function loadStudioDraft(student) {
  const base = createStudioDraft(student);
  try {
    const saved = JSON.parse(localStorage.getItem(getDraftKey(student)) || 'null');
    return {
      draft: mergeStudioDraft(base, saved),
      restoredAt: saved?.updatedAt || null,
    };
  } catch {
    return { draft: base, restoredAt: null };
  }
}

function useAutosavedStudioDraft(student) {
  const [loadedKey, setLoadedKey] = useState(getDraftKey(student));
  const [loadedAt, setLoadedAt] = useState(null);
  const [draft, setDraft] = useState(() => {
    const loaded = loadStudioDraft(student);
    return loaded.draft;
  });
  const [saveState, setSaveState] = useState({ label: 'Ready', detail: 'Local autosave initialized.' });
  const draftKey = getDraftKey(student);

  useEffect(() => {
    if (loadedKey === draftKey) return;
    const loaded = loadStudioDraft(student);
    setDraft(loaded.draft);
    setLoadedAt(loaded.restoredAt);
    setSaveState({
      label: loaded.restoredAt ? 'Restored' : 'Ready',
      detail: loaded.restoredAt ? `Restored local work from ${new Date(loaded.restoredAt).toLocaleTimeString()}.` : 'No previous local draft found.',
    });
    setLoadedKey(draftKey);
  }, [draftKey, loadedKey, student]);

  useEffect(() => {
    setSaveState({ label: 'Saving', detail: 'Writing comparison draft locally.' });
    const timer = setTimeout(() => {
      const updatedAt = new Date().toISOString();
      localStorage.setItem(draftKey, JSON.stringify({ version: 2, updatedAt, data: draft }));
      setLoadedAt(updatedAt);
      setSaveState({ label: 'Saved', detail: `Autosaved at ${new Date(updatedAt).toLocaleTimeString()}.` });
    }, 450);
    return () => clearTimeout(timer);
  }, [draft, draftKey]);

  return { draft, setDraft, saveState, loadedAt, draftKey };
}

function buildSharedSaveFields(student) {
  return {
    projectId: student?.projectId || MEGA_PROJECT_ID,
    period: String(student?.period || ''),
    career: student?.assignedCareer || student?.career || student?.careerLed || '',
    assignedCareer: student?.assignedCareer || student?.career || student?.careerLed || '',
    podDocId: getMegaPodDocId(student),
    legacyPodDocId: getLegacyPodDocId(student),
    alias: student?.alias || student?.id || '',
    studentId: student?.id || '',
    updatedAt: serverTimestamp(),
  };
}

async function uploadPresentationAsset(student, file, mediaType) {
  const cleanName = file.name.replace(/[^a-zA-Z0-9._-]+/g, '-');
  const path = `presentations/${MEGA_PROJECT_ID}/${student?.period || 'period'}/${student?.id || student?.alias || 'student'}/${Date.now()}-${cleanName}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file, {
    contentType: file.type || undefined,
    customMetadata: {
      projectId: MEGA_PROJECT_ID,
      period: String(student?.period || ''),
      mediaType,
    },
  });
  return getDownloadURL(storageRef);
}

function useLocalStudioDraftSnapshot(student) {
  const [snapshot, setSnapshot] = useState(() => loadStudioDraft(student).draft);
  const refresh = () => setSnapshot(loadStudioDraft(student).draft);

  useEffect(() => {
    setSnapshot(loadStudioDraft(student).draft);
  }, [student]);

  return { snapshot, refresh };
}

function inferRecordPeriod(record) {
  if (record?.period) return String(record.period);
  const id = `${record?.id || ''}`;
  const parts = id.split('_');
  if (parts[0] === MEGA_PROJECT_ID && parts[1]) return String(parts[1]);
  if (parts[0]) return String(parts[0]);
  return '';
}

function sameCareer(record, student) {
  const myCareer = `${student?.assignedCareer || student?.career || student?.careerLed || ''}`.toLowerCase();
  if (!myCareer) return true;
  const recordText = `${record?.assignedCareer || record?.career || record?.selectedCareer || record?.podName || record?.pod || record?.id || ''}`.toLowerCase();
  return recordText.includes(myCareer) || myCareer.includes(recordText);
}

function pctFromChecks(checks) {
  if (!checks.length) return 0;
  const earned = checks.filter(Boolean).length;
  return Math.round((earned / checks.length) * 100);
}

function textReady(value, min = 30) {
  return `${value || ''}`.trim().length >= min;
}

function computeBrainstormProgress(data = {}) {
  const careers = Array.isArray(data.careers) ? data.careers.slice(0, 3) : [];
  const checks = [];
  for (let index = 0; index < 3; index += 1) {
    const career = careers[index] || {};
    checks.push(textReady(career.idea, 8));
    checks.push(textReady(career.pros, 20));
    checks.push(textReady(career.cons, 20));
  }
  checks.push(textReady(data.selectedCareer, 2));
  checks.push(textReady(data.pitch, 60));
  return pctFromChecks(checks);
}

function computeResearchProgress(data = {}) {
  const responsibilities = Array.isArray(data.responsibilities) ? data.responsibilities : [];
  const vocab = Array.isArray(data.vocab) ? data.vocab : [];
  const sources = Array.isArray(data.sources) ? data.sources : [];
  return pctFromChecks([
    textReady(data.jobDescription, 40),
    ...[0, 1, 2, 3, 4].map((index) => textReady(responsibilities[index], 25)),
    textReady(data.education, 25),
    textReady(data.salaryRange?.min, 2) && textReady(data.salaryRange?.max, 2),
    textReady(data.coreConnection, 40),
    textReady(data.realWorldExample, 35),
    ...[0, 1, 2].map((index) => textReady(vocab[index]?.term, 3) && textReady(vocab[index]?.use, 20)),
    ...[0, 1].map((index) => textReady(sources[index]?.title, 4) && textReady(sources[index]?.url, 8)),
    textReady(data.surprisingFact, 30),
  ]);
}

function computeScriptProgress(data = {}) {
  const script = data.script || {};
  const storyboard = Array.isArray(data.storyboard) ? data.storyboard : [];
  const locationPlan = data.locationPlan || {};
  return pctFromChecks([
    textReady(script.hook, 30),
    textReady(script.intro, 40),
    textReady(script.scienceInAction, 60),
    textReady(script.impact, 40),
    textReady(script.closing, 25),
    ...[0, 1, 2, 3, 4].map((index) => textReady(storyboard[index]?.says, 20) && textReady(storyboard[index]?.sees, 20)),
    ...SCRIPT_BEATS.map(([key]) => Array.isArray(locationPlan[key]?.places) && locationPlan[key].places.length > 0 && textReady(locationPlan[key]?.justification, 20)),
  ]);
}

function computeMediaProgress(data = {}) {
  const visuals = Array.isArray(data.visualEvidence) ? data.visualEvidence : [];
  return pctFromChecks([
    textReady(data.slidePlan, 40),
    ...[0, 1, 2].map((index) => textReady(visuals[index], 20)),
    Boolean(data.pdfReady),
    Boolean(data.videoReady),
    textReady(data.exportNotes, 25),
    Boolean(data.fileUrl || data.fileName || data.videoFileUrl || data.videoFileName || data.slidesUrl),
  ]);
}

function computeReflectionProgress(data = {}) {
  return pctFromChecks([
    Boolean(data.selfScores),
    textReady(data.contribution, 35),
    textReady(data.challenge, 25),
    data.teamIssues ? textReady(data.teamExplanation, 20) : true,
  ]);
}

function computeDraftProgress(draft) {
  const moduleProgress = {
    brainstorm: computeBrainstormProgress(draft.brainstorm),
    research: computeResearchProgress(draft.research),
    script: computeScriptProgress(draft.script),
    slides: computeMediaProgress(draft.media),
    recording: computeReflectionProgress(draft.reflection),
  };
  const overall = Math.round(Object.values(moduleProgress).reduce((sum, value) => sum + value, 0) / Object.values(moduleProgress).length);
  return { moduleProgress, overall };
}

function computeFirestoreProgress(docs, period, student) {
  const periodFilter = (record) => inferRecordPeriod(record) === String(period);
  const careerFilter = (record) => sameCareer(record, student);
  const brainstorms = docs.brainstorms.filter((record) => periodFilter(record) && careerFilter(record));
  const research = docs.research.filter((record) => periodFilter(record) && careerFilter(record));
  const scripts = docs.scripts.filter((record) => periodFilter(record) && careerFilter(record));
  const presentations = docs.presentations.filter((record) => periodFilter(record) && careerFilter(record));
  const max = (records, fn) => records.reduce((best, record) => Math.max(best, fn(record)), 0);
  const moduleProgress = {
    brainstorm: max(brainstorms, computeBrainstormProgress),
    research: max(research, computeResearchProgress),
    script: max(scripts, computeScriptProgress),
    slides: max(presentations, computeMediaProgress),
    recording: presentations.some((record) => record.videoFileUrl || record.videoFileName) ? 100 : 0,
  };
  const overall = Math.round(Object.values(moduleProgress).reduce((sum, value) => sum + value, 0) / Object.values(moduleProgress).length);
  return { moduleProgress, overall };
}

function useLeadershipProgress(student, localDraft) {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState({
    students: [],
    brainstorms: [],
    research: [],
    scripts: [],
    presentations: [],
  });
  const [refreshIndex, setRefreshIndex] = useState(0);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      const rows = (snapshot) => snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
      const safeRows = async (collectionName) => {
        try {
          return rows(await getDocs(collection(db, collectionName)));
        } catch {
          return [];
        }
      };
      const [students, brainstorms, research, scripts, presentations] = await Promise.all([
        safeRows('sa_students'),
        safeRows('sa_brainstorms'),
        safeRows('sa_research'),
        safeRows('sa_scripts'),
        safeRows('sa_presentations'),
      ]);
      if (!active) return;
      setRecords({
        students,
        brainstorms,
        research,
        scripts,
        presentations,
      });
      if (active) setLoading(false);
    };
    load();
    return () => {
      active = false;
    };
  }, [refreshIndex]);

  const localProgress = computeDraftProgress(localDraft);
  const periods = Object.keys(PERIOD_CHAPTER_ASSIGNMENTS).map((period) => {
    const liveProgress = computeFirestoreProgress(records, period, student);
    const isOwnPeriod = String(student?.period) === String(period);
    const moduleProgress = { ...liveProgress.moduleProgress };
    if (isOwnPeriod) {
      Object.keys(moduleProgress).forEach((key) => {
        moduleProgress[key] = Math.max(moduleProgress[key], localProgress.moduleProgress[key] || 0);
      });
    }
    const values = Object.values(moduleProgress);
    const overall = Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
    const roster = records.students.filter((row) => String(row.period) === String(period));
    return {
      period,
      roster,
      leaders: roster.filter((row) => row.boardMember || row.podLeader || row.careerLeader).length,
      chapter: PERIOD_CHAPTER_ASSIGNMENTS[period],
      moduleProgress,
      overall,
    };
  });

  return {
    loading,
    students: records.students,
    periods,
    localProgress,
    refresh: () => setRefreshIndex((value) => value + 1),
  };
}

function StudentApp() {
  const [, setUser] = useState(null);
  const [student, setStudent] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [previewMode, setPreviewMode] = useState(false);
  const [activeView, setActiveView] = useState('home');
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((currentUser) => {
      const stored = JSON.parse(localStorage.getItem('studentData') || 'null');
      const role = localStorage.getItem('userRole');
      if (role === 'student' && stored) {
        setUser(currentUser);
        setStudent(stored);
      }
      setAuthLoading(false);
    });
    return () => unsub();
  }, []);

  const handlePreview = () => {
    setPreviewMode(true);
    setStudent(SAMPLE_STUDENT);
    setActiveView('home');
  };

  const handleLoginComplete = (currentUser, studentDoc) => {
    localStorage.setItem('userRole', 'student');
    localStorage.setItem('studentData', JSON.stringify(studentDoc));
    setUser(currentUser);
    setStudent(studentDoc);
    setActiveView('home');
  };

  const handleLogout = async () => {
    if (!previewMode && auth.currentUser) {
      try {
        await signOut(auth);
      } catch {
        // Emergency local mode should still let students leave while Auth is suspended.
      }
    }
    localStorage.removeItem('userRole');
    localStorage.removeItem('studentData');
    setPreviewMode(false);
    setUser(null);
    setStudent(null);
  };

  if (authLoading) return <StudentLoading />;

  if (!student) {
    return <StudentLogin onPreview={handlePreview} onComplete={handleLoginComplete} />;
  }

  return (
    <div className="student-lab-shell">
      <header className="student-topbar">
        <button className="icon-button mobile-only" onClick={() => setMobileOpen(true)} aria-label="Open menu">
          <Menu size={20} />
        </button>
        <div className="brand-lockup">
          <img src="/drgl-logo.png" alt="Dr. GL" />
          <div>
            <strong>Science Ambassador Hub</strong>
            <span>{previewMode ? 'Student preview lab' : student?.localOnly ? 'Emergency local workspace' : 'Student live comparison'}</span>
          </div>
        </div>
        <button className="ghost-button" onClick={handleLogout}>
          <LogOut size={16} />
          Exit
        </button>
      </header>

      <div className="student-layout">
        <aside className={`student-nav ${mobileOpen ? 'open' : ''}`}>
          <StudentIdentity student={student} previewMode={previewMode} />
          {[
            ['home', 'Home base', Target],
            ['calendar', 'Calendar', ClipboardList],
            ['workboard', 'Workboard', ClipboardList],
            ['studio', 'Studio', PlayCircle],
            ...(hasLeadershipRole(student) ? [['leadership', 'Leadership', Users]] : []),
            ['guide', 'Guide', BookOpen],
            ['reflection', 'Reflection', Award],
          ].map(([id, label, Icon]) => (
            <button key={id} className={activeView === id ? 'active' : ''} onClick={() => {
              setActiveView(id);
              setMobileOpen(false);
            }}>
              <Icon size={18} />
              {label}
            </button>
          ))}
        </aside>

        {mobileOpen && <button className="scrim" onClick={() => setMobileOpen(false)} aria-label="Close menu"><X size={18} /></button>}

        <main className="student-content">
          {activeView === 'home' && <StudentHome student={student} setActiveView={setActiveView} />}
          {activeView === 'calendar' && <StudentCalendar student={student} />}
          {activeView === 'workboard' && <StudentWorkboard student={student} setActiveView={setActiveView} />}
          {activeView === 'studio' && <StudentStudio student={student} />}
          {activeView === 'leadership' && <StudentLeadership student={student} />}
          {activeView === 'guide' && <StudentGuide student={student} />}
          {activeView === 'reflection' && <StudentReflection student={student} />}
        </main>
      </div>
    </div>
  );
}

function StudentLogin({ onPreview, onComplete }) {
  const [step, setStep] = useState('period');
  const [period, setPeriod] = useState('');
  const [aliases, setAliases] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [offlineAlias, setOfflineAlias] = useState('');
  const [offlineCareer, setOfflineCareer] = useState('');
  const selectedStudent = aliases.find((item) => item.id === selectedId);

  const loadPeriod = async (selectedPeriod) => {
    setPeriod(selectedPeriod);
    setSelectedId('');
    setPin('');
    setOfflineAlias('');
    setOfflineCareer('');
    setError('');
    setLoading(true);
    try {
      if (!auth.currentUser) await signInAnonymously(auth);
      const q = query(collection(db, 'sa_students'), where('period', '==', selectedPeriod));
      const snapshot = await getDocs(q);
      const rows = snapshot.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .sort((a, b) => `${a.alias || a.id}`.localeCompare(`${b.alias || b.id}`));
      setAliases(rows);
      if (!rows.length) {
        setStep('offline');
        setError('No aliases are loaded for this period yet in the new Firebase project. Use the local workspace while the roster is being migrated.');
        return;
      }
      setStep('alias');
    } catch (err) {
      if (isAuthServiceBlocked(err)) {
        setStep('offline');
        setError('Firebase sign-in is temporarily unavailable. Use the emergency local workspace so your draft autosaves on this device.');
      } else {
        setError(err.message);
      }
    }
    setLoading(false);
  };

  const finishLogin = async () => {
    if (!selectedStudent) {
      setError('Choose your alias first.');
      return;
    }
    if (selectedStudent.pin && selectedStudent.pin !== pin) {
      setError('That PIN does not match this alias.');
      return;
    }
    try {
      if (!auth.currentUser) await signInAnonymously(auth);
      onComplete(auth.currentUser, selectedStudent);
    } catch (err) {
      if (isAuthServiceBlocked(err)) {
        setStep('offline');
        setError('Firebase sign-in is temporarily unavailable. Use the emergency local workspace so your draft autosaves on this device.');
      } else {
        setError(err.message);
      }
    }
  };

  const finishOfflineLogin = () => {
    if (!period) {
      setError('Choose your period first.');
      return;
    }
    if (!offlineAlias.trim()) {
      setError('Enter the alias you use in class.');
      return;
    }
    const studentDoc = buildLocalStudent(period, offlineAlias, offlineCareer);
    onComplete({ uid: studentDoc.id, isAnonymous: true }, studentDoc);
  };

  return (
    <div className="student-login">
      <section className="login-panel">
        <div className="brand-lockup large">
          <img src="/drgl-logo.png" alt="Dr. GL" />
          <div>
            <strong>Science Ambassador Hub</strong>
            <span>Student experience lab</span>
          </div>
        </div>
        <h1>A clearer live workspace for students.</h1>
        <p>
          The student version should feel like a guided production dashboard, not a stack
          of assignment forms. Students should know their role, next action, team lane,
          and what "done" looks like as soon as they enter.
        </p>

        {error && <div className="inline-alert warning"><Lock size={18} /><div><strong>Login issue</strong><p>{error}</p></div></div>}

        {step === 'period' && (
          <div className="choice-grid">
            {PERIODS.map((item) => (
              <button key={item} onClick={() => loadPeriod(item)} disabled={loading}>
                <span>Period</span>
                <strong>{item}</strong>
              </button>
            ))}
          </div>
        )}

        {step === 'alias' && (
          <div className="student-form-stack">
            <button className="ghost-button" onClick={() => setStep('period')}>Change period</button>
            <div className="login-context-line">Period {period}</div>
            <label>
              <span>Find your alias</span>
              <select value={selectedId} onChange={(event) => {
                setSelectedId(event.target.value);
                setPin('');
                setError('');
              }}>
                <option value="">Select an alias</option>
                {aliases.map((alias) => (
                  <option key={alias.id} value={alias.id}>{alias.alias || alias.id}</option>
                ))}
              </select>
            </label>
            {selectedStudent?.pin && (
              <label>
                <span>PIN</span>
                <input value={pin} onChange={(event) => setPin(event.target.value)} type="password" placeholder="Enter your PIN" />
              </label>
            )}
            <button className="primary-button" onClick={finishLogin}>
              Enter workspace
              <ArrowRight size={16} />
            </button>
          </div>
        )}

        {step === 'offline' && (
          <div className="student-form-stack">
            <button className="ghost-button" onClick={() => setStep('period')}>Change period</button>
            <div className="login-context-line">Period {period} emergency local workspace</div>
            <label>
              <span>Class alias</span>
              <input value={offlineAlias} onChange={(event) => {
                setOfflineAlias(event.target.value);
                setError('');
              }} placeholder="Enter the alias you use in class" />
            </label>
            <label>
              <span>Career or topic</span>
              <input value={offlineCareer} onChange={(event) => setOfflineCareer(event.target.value)} placeholder="Optional if you know it" />
            </label>
            <button className="primary-button" onClick={finishOfflineLogin}>
              Enter local workspace
              <ArrowRight size={16} />
            </button>
          </div>
        )}
      </section>

      <section className="login-preview-grid">
        <MetricCard icon={Target} label="Role-aware" value="Next action" helper="Students see what matters for their assigned role." />
        <MetricCard icon={Users} label="Team-first" value="Pod context" helper="The class segment and career lane stay visible." />
        <MetricCard icon={ShieldCheck} label="Private" value="Alias-first" helper="The interface can stay student-safe and public-screen ready." />
        <button className="secondary-button wide" onClick={onPreview}>
          <Sparkles size={18} />
          Preview as sample student
        </button>
      </section>
    </div>
  );
}

function StudentIdentity({ student, previewMode }) {
  const roles = getRoles(student);
  const identityLabel = previewMode ? 'Preview student' : student?.localOnly ? 'Saved locally' : 'Signed in';
  return (
    <div className="student-identity-card">
      <span>{identityLabel}</span>
      <strong>{student.alias || student.id}</strong>
      <p>Period {student.period || '-'} - {student.assignedCareer || student.career || 'Career pending'}</p>
      <div className="role-chip-row">
        {roles.map((role) => <span key={role}>{role}</span>)}
      </div>
    </div>
  );
}

function StudentHome({ student, setActiveView }) {
  const chapter = PERIOD_CHAPTER_ASSIGNMENTS[String(student.period)] || {};
  const roles = getRoles(student);
  const recommended = recommendedModules(roles);
  const today = getTodayCalendarRow(new Date()) || getNextCalendarRow(new Date());

  return (
    <div className="student-view-stack">
      <section className="student-hero">
        <span>{MEGA_PROJECT_TITLE}</span>
        <h1>{student.alias || 'Ambassador'}, your production lane is ready.</h1>
        <p>{MEGA_PROJECT_SUBTITLE}. Recording window: {formatProjectWindow(MEGA_PROJECT_WINDOW)}.</p>
        <div className="hero-actions">
          <button className="primary-button" onClick={() => setActiveView('workboard')}>
            Open workboard
            <ArrowRight size={16} />
          </button>
          <button className="secondary-button" onClick={() => setActiveView('guide')}>Review guide</button>
        </div>
      </section>

      <div className="three-column">
        <MetricCard icon={Briefcase} label="Career lane" value={student.assignedCareer || student.career || 'Pending'} helper="Your documentary focus" />
        <MetricCard icon={BookOpen} label="Class segment" value={chapter.chapter || `Period ${student.period}`} helper={chapter.careerLane || chapter.chapterTitle || 'Living World production'} />
        <MetricCard icon={Award} label="Your role" value={roles[0] || 'Team member'} helper={roles.slice(1).join(', ') || 'Primary assignment'} />
      </div>

      <Panel title={today ? `${today.date}: ${today.phase}` : 'Today in the project'} subtitle={today?.work || 'Check the calendar for the next production milestone.'}>
        <div className="role-task-list">
          {roles.map((role) => (
            <div key={role}>
              <span>{role}</span>
              <p>{today?.youDo?.[role] || 'Open Studio and work on the section assigned to your role.'}</p>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Next best actions" subtitle="The live portal should give students a short, role-aware path instead of making them guess where to click.">
        <div className="next-action-grid">
          {recommended.map((mod) => {
            const Icon = mod.icon;
            return (
              <button className="next-action-card" key={mod.id} onClick={() => setActiveView('studio')}>
                <Icon size={22} />
                <strong>{mod.label}</strong>
                <span>{mod.description}</span>
              </button>
            );
          })}
        </div>
      </Panel>

      <Panel title="Progress map" subtitle="Students need a clear finish line for the whole production.">
        <div className="student-progress-map">
          {PROGRESS_SECTIONS.map((section, index) => (
            <div key={section.key} className={index < 2 ? 'complete' : index === 2 ? 'active' : ''}>
              <span>{index + 1}</span>
              <strong>{section.label}</strong>
              <p>{index < 2 ? 'In motion' : index === 2 ? 'Current focus' : 'Coming next'}</p>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

function StudentWorkboard({ student, setActiveView }) {
  const roles = getRoles(student);

  return (
    <div className="student-view-stack">
      <HeroBand
        eyebrow="Workboard"
        title="One board for every part of the assignment."
        body="This is the student-side improvement I would make first: all modules stay visible, but the student's role determines what is emphasized."
      />
      <div className="module-board">
        {MODULES.map((mod) => {
          const Icon = mod.icon;
          const recommended = mod.role === 'Everyone' || roles.some((role) => mod.role.toLowerCase().includes(role.toLowerCase().split(' ')[0]));
          return (
            <article className={`module-card ${recommended ? 'recommended' : ''}`} key={mod.id}>
              <div className="module-card-head">
                <Icon size={22} />
                {recommended && <span>Recommended</span>}
              </div>
              <strong>{mod.label}</strong>
              <p>{mod.description}</p>
              <div className="module-card-footer">
                <span>{mod.role}</span>
                <button onClick={() => setActiveView('studio')}>Open</button>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

function StudentCalendar({ student }) {
  const today = getTodayCalendarRow(new Date());
  const next = getNextCalendarRow(new Date());
  const roles = getRoles(student);

  return (
    <div className="student-view-stack">
      <HeroBand
        eyebrow="Calendar"
        title="Daily production plan."
        body="Every project day shows the class focus, whole-team work, recording status, and role-specific responsibilities."
      />
      <div className="three-column">
        <MetricCard icon={Target} label="Today" value={today?.phase || 'No class row'} helper={today?.date || 'Use the next scheduled row'} />
        <MetricCard icon={Briefcase} label="Next project day" value={next?.phase || 'Project closed'} helper={next?.date || formatProjectWindow(MEGA_PROJECT_WINDOW)} />
        <MetricCard icon={Award} label="Your role focus" value={roles[0] || 'Team member'} helper={roles.slice(1).join(', ') || 'Functional lane'} />
      </div>
      <div className="calendar-grid">
        {MEGA_CALENDAR.map((row) => {
          const isToday = today?.iso === row.iso;
          const isNext = !today && next?.iso === row.iso;
          return (
            <article className={`calendar-card ${isToday ? 'today' : ''} ${isNext ? 'next' : ''}`} key={row.iso}>
              <div className="calendar-card-head">
                <div>
                  <span>{row.date}</span>
                  <strong>{row.phase}</strong>
                </div>
                {row.recording && <span className="recording-badge">Recording</span>}
              </div>
              <p>{row.work}</p>
              <div className="info-line"><span>Mini lesson</span><strong>{row.miniLesson}</strong></div>
              <div className="role-task-list">
                {roles.map((role) => (
                  <div key={`${row.iso}-${role}`}>
                    <span>{role}</span>
                    <p>{row.youDo?.[role] || 'Check your team board and support the current production milestone.'}</p>
                  </div>
                ))}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

function StudentLeadership({ student }) {
  const tracks = getLeadershipTracks(student);
  const [activeTrack, setActiveTrack] = useState(tracks[0]?.id || 'pod');
  const { snapshot, refresh: refreshLocalDraft } = useLocalStudioDraftSnapshot(student);
  const progress = useLeadershipProgress(student, snapshot);

  useEffect(() => {
    if (!tracks.some((track) => track.id === activeTrack)) {
      setActiveTrack(tracks[0]?.id || 'pod');
    }
  }, [activeTrack, tracks]);

  const refreshAll = () => {
    refreshLocalDraft();
    progress.refresh();
  };

  if (!tracks.length) {
    return (
      <div className="student-view-stack">
        <HeroBand
          eyebrow="Leadership"
          title="No leadership assignment is attached to this alias."
          body="Students only see leadership workspaces when they are marked as SAP Board, Pod Leader, or Career Leader."
        />
      </div>
    );
  }

  return (
    <div className="student-view-stack">
      <HeroBand
        eyebrow="Leadership"
        title="Leadership tracking that moves with the work."
        body="Board, pod, and career leads get their own progress surfaces while keeping their functional role tabs."
      />
      <div className="leadership-header">
        <div className="leadership-tabs">
          {tracks.map((track) => (
            <button key={track.id} className={activeTrack === track.id ? 'active' : ''} onClick={() => setActiveTrack(track.id)}>
              <span>{track.label}</span>
              <small>{track.scope}</small>
            </button>
          ))}
        </div>
        <button className="secondary-button" onClick={refreshAll}>{progress.loading ? 'Refreshing...' : 'Refresh progress'}</button>
      </div>
      {activeTrack === 'board' && <BoardTrack student={student} progress={progress} />}
      {activeTrack === 'pod' && <PodLeaderTrack student={student} progress={progress} />}
      {activeTrack === 'career' && <CareerLeaderTrack student={student} progress={progress} />}
    </div>
  );
}

function BoardTrack({ progress }) {
  return (
    <Panel title="SAP Board screen" subtitle="Project-wide period health, calculated from saved module fields and live roster records.">
      <div className="leadership-period-grid">
        {progress.periods.map((period) => (
          <article className="leadership-period-card" key={period.period}>
            <div className="calendar-card-head">
              <div>
                <span>Period {period.period}</span>
                <strong>{period.chapter.chapterTitle}</strong>
              </div>
              <div className="period-score">{period.overall}%</div>
            </div>
            <p>{period.chapter.careerLane}</p>
            <ProgressMeter label="Overall" value={period.overall} />
            <div className="module-progress-list">
              {PROGRESS_SECTIONS.map((section) => (
                <ProgressMeter key={section.key} label={section.label} value={period.moduleProgress[section.key] || 0} compact />
              ))}
            </div>
            <div className="mini-metrics">
              <span>{period.roster.length} students</span>
              <span>{period.leaders} leaders</span>
            </div>
          </article>
        ))}
      </div>
    </Panel>
  );
}

function PodLeaderTrack({ student, progress }) {
  const period = progress.periods.find((item) => String(item.period) === String(student.period)) || progress.periods[0];
  const roster = period?.roster || [];
  return (
    <div className="two-column">
      <Panel title={`Pod Leader: Period ${student.period}`} subtitle="Track your period's assigned production lane and blockers.">
        <div className="module-progress-list roomy">
          {PROGRESS_SECTIONS.map((section) => (
            <ProgressMeter key={section.key} label={section.label} value={period?.moduleProgress[section.key] || 0} />
          ))}
        </div>
      </Panel>
      <Panel title="Pod roster pulse" subtitle="Leader, role, and opt-out details stay visible while the project is active.">
        <div className="leader-roster">
          {roster.slice(0, 14).map((row) => (
            <div key={row.id}>
              <strong>{row.alias || row.id}</strong>
              <span>{getRoles(row).join(', ') || 'Role pending'}</span>
              <p>{row.assignedCareer || row.career || 'Career pending'}{row.onScreenAllowed === false ? ' - on-screen opt-out' : ''}</p>
            </div>
          ))}
          {!roster.length && <p className="body-copy">Roster records will appear here after the period is loaded from Firebase.</p>}
        </div>
      </Panel>
    </div>
  );
}

function CareerLeaderTrack({ student, progress }) {
  const period = progress.periods.find((item) => String(item.period) === String(student.period));
  const career = student.careerLed || student.assignedCareer || student.career || 'Career lane';
  const careerRoster = (period?.roster || []).filter((row) => sameCareer(row, { ...student, assignedCareer: career }));
  return (
    <div className="two-column">
      <Panel title={`Career Leader: ${career}`} subtitle="Own the handoff from brainstorm to research, script, slides, and recording readiness.">
        <div className="module-progress-list roomy">
          {PROGRESS_SECTIONS.map((section) => (
            <ProgressMeter key={section.key} label={section.label} value={period?.moduleProgress[section.key] || 0} />
          ))}
        </div>
      </Panel>
      <Panel title="Career team" subtitle="Functional roles remain visible for the career leader.">
        <div className="leader-roster">
          {careerRoster.slice(0, 12).map((row) => (
            <div key={row.id}>
              <strong>{row.alias || row.id}</strong>
              <span>{getRoles(row).join(', ') || 'Role pending'}</span>
              <p>{row.podLeader ? 'Pod Leader' : row.careerLeader ? 'Career Leader' : 'Team member'}</p>
            </div>
          ))}
          {!careerRoster.length && <p className="body-copy">Career roster records will appear here after students claim assignments.</p>}
        </div>
      </Panel>
    </div>
  );
}

function StudentStudio({ student }) {
  const roles = getRoles(student);
  const firstModule = recommendedModules(roles).find((module) => module.id !== 'mission') || WORKSPACE_MODULES[0];
  const [activeModule, setActiveModule] = useState(firstModule.id);
  const { draft, setDraft, saveState, loadedAt } = useAutosavedStudioDraft(student);
  const [remoteStatus, setRemoteStatus] = useState('');
  const [uploading, setUploading] = useState('');

  const saveLocalDraftNow = (nextDraft = draft) => {
    const updatedAt = new Date().toISOString();
    localStorage.setItem(getDraftKey(student), JSON.stringify({ version: 2, updatedAt, data: nextDraft }));
  };

  const updateSection = (section, key, value) => {
    setDraft((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [key]: value,
      },
    }));
  };

  const updateNested = (section, group, key, value) => {
    setDraft((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [group]: {
          ...current[section][group],
          [key]: value,
        },
      },
    }));
  };

  const updateListValue = (section, listName, index, value) => {
    setDraft((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [listName]: current[section][listName].map((item, itemIndex) => (itemIndex === index ? value : item)),
      },
    }));
  };

  const updateListObject = (section, listName, index, key, value) => {
    setDraft((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [listName]: current[section][listName].map((item, itemIndex) => (
          itemIndex === index ? { ...item, [key]: value } : item
        )),
      },
    }));
  };

  const updateLocationPlan = (beatKey, key, value) => {
    setDraft((current) => ({
      ...current,
      script: {
        ...current.script,
        locationPlan: {
          ...current.script.locationPlan,
          [beatKey]: {
            ...(current.script.locationPlan?.[beatKey] || {}),
            [key]: value,
          },
        },
      },
    }));
  };

  const saveScriptPlan = async () => {
    saveLocalDraftNow();
    if (student?.localOnly || !auth.currentUser) {
      setRemoteStatus('Saved locally on this device. Teacher cloud preview will resume when Firebase Auth is restored.');
      return;
    }
    setRemoteStatus('Saving script and shoot plan for teacher preview...');
    try {
      await setDoc(doc(db, 'sa_scripts', getMegaPodDocId(student)), {
        ...buildSharedSaveFields(student),
        script: draft.script.script,
        storyboard: draft.script.storyboard,
        locationPlan: draft.script.locationPlan,
        status: 'In progress',
      }, { merge: true });
      setRemoteStatus('Script and shoot plan saved for the teacher dashboard.');
    } catch (err) {
      if (isAuthServiceBlocked(err)) {
        setRemoteStatus('Saved locally on this device. Firebase Auth is blocking teacher cloud preview right now.');
      } else {
        setRemoteStatus(`Could not save script plan: ${err.message}`);
      }
    }
  };

  const saveMediaPlan = async (mediaPatch = {}) => {
    const nextMedia = { ...draft.media, ...mediaPatch };
    const nextDraft = { ...draft, media: nextMedia };
    setDraft((current) => ({
      ...current,
      media: { ...current.media, ...mediaPatch },
    }));
    saveLocalDraftNow(nextDraft);
    if (student?.localOnly || !auth.currentUser) {
      setRemoteStatus('Media plan saved locally on this device. Cloud upload/teacher preview will resume when Firebase Auth is restored.');
      return;
    }
    try {
      await setDoc(doc(db, 'sa_presentations', getMegaPodDocId(student)), {
        ...buildSharedSaveFields(student),
        ...nextMedia,
        status: nextMedia.fileUrl || nextMedia.videoFileUrl ? 'Ready for review' : 'In progress',
      }, { merge: true });
      setRemoteStatus('Media plan saved for teacher preview.');
    } catch (err) {
      if (isAuthServiceBlocked(err)) {
        setRemoteStatus('Media plan saved locally on this device. Firebase Auth is blocking teacher cloud preview right now.');
      } else {
        setRemoteStatus(`Could not save media plan: ${err.message}`);
      }
    }
  };

  const uploadMediaFile = async (file, mediaType) => {
    if (!file) return;
    setUploading(mediaType);
    setRemoteStatus(`Uploading ${mediaType === 'deck' ? 'deck' : 'video'}...`);
    if (student?.localOnly || !auth.currentUser) {
      const patch = mediaType === 'deck'
        ? { fileName: file.name, pdfReady: true, localFilePendingUpload: true }
        : { videoFileName: file.name, videoReady: true, localVideoPendingUpload: true };
      await saveMediaPlan(patch);
      setRemoteStatus(`${mediaType === 'deck' ? 'Deck' : 'Video'} name saved locally. Keep the file available so it can be uploaded after Firebase Auth is restored.`);
      setUploading('');
      return;
    }
    try {
      const url = await uploadPresentationAsset(student, file, mediaType);
      const patch = mediaType === 'deck'
        ? { fileName: file.name, fileUrl: url, pdfReady: true }
        : { videoFileName: file.name, videoFileUrl: url, videoReady: true };
      await saveMediaPlan(patch);
      setRemoteStatus(`${mediaType === 'deck' ? 'Deck' : 'Video'} uploaded and visible to the teacher.`);
    } catch (err) {
      if (isAuthServiceBlocked(err)) {
        const patch = mediaType === 'deck'
          ? { fileName: file.name, pdfReady: true, localFilePendingUpload: true }
          : { videoFileName: file.name, videoReady: true, localVideoPendingUpload: true };
        await saveMediaPlan(patch);
        setRemoteStatus(`${mediaType === 'deck' ? 'Deck' : 'Video'} name saved locally. Firebase Auth is blocking upload right now.`);
      } else {
        setRemoteStatus(`Upload failed: ${err.message}`);
      }
    }
    setUploading('');
  };

  const renderWorkspace = () => {
    if (activeModule === 'brainstorm') {
      return (
        <BrainstormWorkspace
          data={draft.brainstorm}
          updateSection={updateSection}
          updateListObject={updateListObject}
        />
      );
    }
    if (activeModule === 'script') {
      return (
        <ScriptWorkspace
          student={student}
          data={draft.script}
          updateNested={updateNested}
          updateListObject={updateListObject}
          updateLocationPlan={updateLocationPlan}
          saveScriptPlan={saveScriptPlan}
          remoteStatus={remoteStatus}
        />
      );
    }
    if (activeModule === 'media') {
      return (
        <MediaWorkspace
          data={draft.media}
          updateSection={updateSection}
          updateListValue={updateListValue}
          uploadMediaFile={uploadMediaFile}
          saveMediaPlan={saveMediaPlan}
          remoteStatus={remoteStatus}
          uploading={uploading}
        />
      );
    }
    if (activeModule === 'reflection') {
      return (
        <ReflectionWorkspace
          data={draft.reflection}
          updateSection={updateSection}
          updateNested={updateNested}
        />
      );
    }
    return (
      <ResearchWorkspace
        data={draft.research}
        updateSection={updateSection}
        updateNested={updateNested}
        updateListValue={updateListValue}
        updateListObject={updateListObject}
      />
    );
  };

  return (
    <div className="student-view-stack">
      <HeroBand
        eyebrow="Studio"
        title="Build the part your team needs next."
        body="Work through each section, use the tips on the side, and save scripts or media when your teacher needs to preview them."
      />

      <div className="preservation-banner">
        <div>
          <ShieldCheck size={22} />
          <div>
            <strong>Autosave is on</strong>
            <span>Your writing is saved on this device while you work. Use the save or upload buttons when your teacher needs to preview script plans or media.</span>
          </div>
        </div>
        <div className="autosave-status">
          <span>{saveState.label}</span>
          <strong>{saveState.detail}</strong>
          {loadedAt && <small>Local draft timestamp: {new Date(loadedAt).toLocaleString()}</small>}
        </div>
      </div>

      <div className="studio-shell">
        <aside className="studio-module-tabs" aria-label="Studio modules">
          {WORKSPACE_MODULES.map((mod) => {
            const Icon = mod.icon;
            const recommended = mod.id === firstModule.id;
            return (
              <button
                key={mod.id}
                className={activeModule === mod.id ? 'active' : ''}
                onClick={() => setActiveModule(mod.id)}
              >
                <Icon size={18} />
                <span>{mod.label}</span>
                {recommended && <small>Role focus</small>}
              </button>
            );
          })}
        </aside>

        <div className="studio-main">
          {renderWorkspace()}
        </div>

        <aside className="studio-side-rail">
          <StudentStudioTips activeModule={activeModule} remoteStatus={remoteStatus} />
        </aside>
      </div>
    </div>
  );
}

function StudentStudioTips({ activeModule, remoteStatus }) {
  const tips = SECTION_TIPS[activeModule] || SECTION_TIPS.research;
  const titleMap = {
    brainstorm: 'Brainstorm tips',
    research: 'Research tips',
    script: 'Script and recording tips',
    media: 'Media upload tips',
    reflection: 'Reflection tips',
  };

  return (
    <>
      <Panel title={titleMap[activeModule] || 'Work tips'} subtitle="Use these while you work on this section.">
        <div className="quality-list">
          {tips.map((tip) => <CheckLine key={tip} text={tip} />)}
        </div>
      </Panel>
      <Panel title="Autosave and teacher preview" subtitle="Your draft is kept on this device. Use the save/upload buttons when your teacher needs to see it.">
        <div className="quality-list">
          <CheckLine text="Draft text autosaves in this browser." />
          <CheckLine text="Script shoot plans save to the teacher dashboard." />
          <CheckLine text="Uploaded media files can be previewed by the teacher." />
        </div>
        {remoteStatus && <div className="save-note block">{remoteStatus}</div>}
      </Panel>
    </>
  );
}

function BrainstormWorkspace({ data, updateSection, updateListObject }) {
  return (
    <Panel title="Career brainstorm" subtitle="Same old brainstorm shape, but broken into visible decision steps.">
      <div className="granular-grid">
        {data.careers.map((career, index) => (
          <div className="granular-card" key={`career-${index + 1}`}>
            <div className="granular-card-head">
              <span>Option {index + 1}</span>
              <strong>Career candidate</strong>
            </div>
            <label>
              <span>Career idea</span>
              <input value={career.idea} onChange={(event) => updateListObject('brainstorm', 'careers', index, 'idea', event.target.value)} />
            </label>
            <label>
              <span>Why it works</span>
              <textarea value={career.pros} onChange={(event) => updateListObject('brainstorm', 'careers', index, 'pros', event.target.value)} />
            </label>
            <label>
              <span>What needs checking</span>
              <textarea value={career.cons} onChange={(event) => updateListObject('brainstorm', 'careers', index, 'cons', event.target.value)} />
            </label>
          </div>
        ))}
      </div>
      <div className="form-section">
        <label>
          <span>Selected career</span>
          <input value={data.selectedCareer} onChange={(event) => updateSection('brainstorm', 'selectedCareer', event.target.value)} />
        </label>
        <label>
          <span>Team pitch</span>
          <textarea value={data.pitch} onChange={(event) => updateSection('brainstorm', 'pitch', event.target.value)} />
        </label>
      </div>
    </Panel>
  );
}

function ResearchWorkspace({ data, updateSection, updateNested, updateListValue, updateListObject }) {
  return (
    <Panel title="Research packet" subtitle="Research needs enough structure for students to actually build the segment, not just paste notes.">
      <div className="form-section">
        <label>
          <span>1. Job description in student language</span>
          <textarea value={data.jobDescription} onChange={(event) => updateSection('research', 'jobDescription', event.target.value)} placeholder="What does this career do every day?" />
        </label>
      </div>

      <div className="granular-grid compact">
        {data.responsibilities.map((item, index) => (
          <label className="granular-card" key={`responsibility-${index + 1}`}>
            <span>{index + 2}. Responsibility {index + 1}</span>
            <textarea value={item} onChange={(event) => updateListValue('research', 'responsibilities', index, event.target.value)} placeholder="Describe one real task." />
          </label>
        ))}
      </div>

      <div className="form-section two-up">
        <label>
          <span>7. Education or training path</span>
          <textarea value={data.education} onChange={(event) => updateSection('research', 'education', event.target.value)} />
        </label>
        <label>
          <span>8. Chapter science connection</span>
          <textarea value={data.coreConnection} onChange={(event) => updateSection('research', 'coreConnection', event.target.value)} />
        </label>
      </div>

      <div className="form-section two-up">
        <label>
          <span>9. Salary range minimum</span>
          <input value={data.salaryRange.min} onChange={(event) => updateNested('research', 'salaryRange', 'min', event.target.value)} placeholder="$" />
        </label>
        <label>
          <span>10. Salary range maximum</span>
          <input value={data.salaryRange.max} onChange={(event) => updateNested('research', 'salaryRange', 'max', event.target.value)} placeholder="$" />
        </label>
      </div>

      <div className="form-section two-up">
        <label>
          <span>11. Real-world example</span>
          <textarea value={data.realWorldExample} onChange={(event) => updateSection('research', 'realWorldExample', event.target.value)} />
        </label>
        <label>
          <span>12. Surprising fact</span>
          <textarea value={data.surprisingFact} onChange={(event) => updateSection('research', 'surprisingFact', event.target.value)} />
        </label>
      </div>

      <div className="granular-grid">
        {data.vocab.map((item, index) => (
          <div className="granular-card" key={`vocab-${index + 1}`}>
            <div className="granular-card-head">
              <span>Vocabulary {index + 1}</span>
              <strong>Use it correctly</strong>
            </div>
            <label>
              <span>Term</span>
              <input value={item.term} onChange={(event) => updateListObject('research', 'vocab', index, 'term', event.target.value)} />
            </label>
            <label>
              <span>How the script will use it</span>
              <textarea value={item.use} onChange={(event) => updateListObject('research', 'vocab', index, 'use', event.target.value)} />
            </label>
          </div>
        ))}
      </div>

      <div className="granular-grid compact">
        {data.sources.map((item, index) => (
          <div className="granular-card" key={`source-${index + 1}`}>
            <div className="granular-card-head">
              <span>Source {index + 1}</span>
              <strong>Evidence trail</strong>
            </div>
            <label>
              <span>Title</span>
              <input value={item.title} onChange={(event) => updateListObject('research', 'sources', index, 'title', event.target.value)} />
            </label>
            <label>
              <span>URL</span>
              <input value={item.url} onChange={(event) => updateListObject('research', 'sources', index, 'url', event.target.value)} />
            </label>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function ScriptWorkspace({ data, updateNested, updateListObject, updateLocationPlan, saveScriptPlan, remoteStatus }) {
  return (
    <Panel title="Script studio" subtitle="Write the narration and plan exactly where each part should be recorded.">
      <div className="granular-grid script-beats">
        {SCRIPT_BEATS.map(([key, label, helper], index) => (
          <label className="granular-card" key={key}>
            <span>Beat {index + 1}: {label}</span>
            <textarea value={data.script[key]} onChange={(event) => updateNested('script', 'script', key, event.target.value)} placeholder={helper} />
            <small>{helper}</small>
          </label>
        ))}
      </div>

      <div className="storyboard-list">
        {data.storyboard.map((scene, index) => (
          <div className="storyboard-row" key={`scene-${index + 1}`}>
            <strong>Scene {index + 1}</strong>
            <label>
              <span>What we say</span>
              <textarea value={scene.says} onChange={(event) => updateListObject('script', 'storyboard', index, 'says', event.target.value)} />
            </label>
            <label>
              <span>What the audience sees</span>
              <textarea value={scene.sees} onChange={(event) => updateListObject('script', 'storyboard', index, 'sees', event.target.value)} />
            </label>
          </div>
        ))}
      </div>

      <div className="new-feature-panel">
        <div className="new-feature-heading">
          <span className="new-feature-badge">New</span>
          <div>
            <h3>Campus shoot plan</h3>
            <p>Choose every place your team wants to record, then justify why that location is needed.</p>
          </div>
        </div>
        <div className="location-plan-grid">
          {SCRIPT_BEATS.map(([key, label]) => {
            const plan = data.locationPlan?.[key] || { places: [], customPlace: '', justification: '' };
            return (
              <div className="location-plan-card" key={`location-${key}`}>
                <div className="granular-card-head">
                  <span>{label}</span>
                  <MapPin size={17} />
                </div>
                <label>
                  <span>Recording location(s)</span>
                  <select
                    multiple
                    value={plan.places || []}
                    onChange={(event) => updateLocationPlan(key, 'places', Array.from(event.target.selectedOptions).map((option) => option.value))}
                  >
                    {SCHOOL_LOCATIONS.map((place) => <option key={place} value={place}>{place}</option>)}
                  </select>
                </label>
                {(plan.places || []).includes('Custom') && (
                  <label>
                    <span>Custom location</span>
                    <input value={plan.customPlace || ''} onChange={(event) => updateLocationPlan(key, 'customPlace', event.target.value)} placeholder="Name the exact place" />
                  </label>
                )}
                <label>
                  <span>Why this place is needed</span>
                  <textarea value={plan.justification || ''} onChange={(event) => updateLocationPlan(key, 'justification', event.target.value)} placeholder="Explain what the audience will see there and why it helps the story." />
                </label>
              </div>
            );
          })}
        </div>
        <div className="studio-actions">
          <button className="primary-button" onClick={saveScriptPlan}>
            <Camera size={16} />
            Save script and shoot plan
          </button>
          {remoteStatus && <span className="save-note">{remoteStatus}</span>}
        </div>
      </div>
    </Panel>
  );
}

function MediaWorkspace({ data, updateSection, updateListValue, uploadMediaFile, saveMediaPlan, remoteStatus, uploading }) {
  return (
    <Panel title="Presentation media" subtitle="Upload the files your teacher needs to preview before recording.">
      <div className="form-section">
        <label>
          <span>Slide or visual plan</span>
          <textarea value={data.slidePlan} onChange={(event) => updateSection('media', 'slidePlan', event.target.value)} />
        </label>
      </div>
      <div className="granular-grid compact">
        {data.visualEvidence.map((item, index) => (
          <label className="granular-card" key={`visual-${index + 1}`}>
            <span>Visual evidence {index + 1}</span>
            <textarea value={item} onChange={(event) => updateListValue('media', 'visualEvidence', index, event.target.value)} />
          </label>
        ))}
      </div>
      <div className="toggle-row">
        <label><input type="checkbox" checked={data.pdfReady} onChange={(event) => updateSection('media', 'pdfReady', event.target.checked)} /> PDF or slide deck ready</label>
        <label><input type="checkbox" checked={data.videoReady} onChange={(event) => updateSection('media', 'videoReady', event.target.checked)} /> Video recording ready</label>
      </div>
      <div className="upload-grid">
        <label className="upload-card">
          <UploadCloud size={22} />
          <strong>Upload deck or PDF</strong>
          <span>{data.fileName || 'Slides, PDF, or visual plan'}</span>
          <input type="file" accept=".pdf,.ppt,.pptx,.key,.png,.jpg,.jpeg" onChange={(event) => uploadMediaFile(event.target.files?.[0], 'deck')} />
        </label>
        <label className="upload-card">
          <Camera size={22} />
          <strong>Upload recording</strong>
          <span>{data.videoFileName || 'Video file when ready'}</span>
          <input type="file" accept="video/*,.mov,.mp4" onChange={(event) => uploadMediaFile(event.target.files?.[0], 'video')} />
        </label>
      </div>
      {(data.fileUrl || data.videoFileUrl) && (
        <div className="media-link-row">
          {data.fileUrl && <a href={data.fileUrl} target="_blank" rel="noopener noreferrer">Preview deck</a>}
          {data.videoFileUrl && <a href={data.videoFileUrl} target="_blank" rel="noopener noreferrer">Preview video</a>}
        </div>
      )}
      <label className="form-section">
        <span>Export notes</span>
        <textarea value={data.exportNotes} onChange={(event) => updateSection('media', 'exportNotes', event.target.value)} />
      </label>
      <div className="studio-actions">
        <button className="primary-button" onClick={() => saveMediaPlan()}>
          <UploadCloud size={16} />
          Save media plan
        </button>
        {(remoteStatus || uploading) && <span className="save-note">{uploading ? 'Uploading...' : remoteStatus}</span>}
      </div>
    </Panel>
  );
}

function ReflectionWorkspace({ data, updateSection, updateNested }) {
  return (
    <Panel title="Reflection and closeout" subtitle="Evaluation drafts must remain separate from submitted evaluations so students can revise without losing work.">
      <div className="score-grid">
        {Object.entries(data.selfScores).map(([key, value]) => (
          <label key={key}>
            <span>{key}</span>
            <input type="range" min="1" max="5" value={value} onChange={(event) => updateNested('reflection', 'selfScores', key, Number(event.target.value))} />
            <strong>{value}/5</strong>
          </label>
        ))}
      </div>
      <div className="form-section two-up">
        <label>
          <span>What did you contribute?</span>
          <textarea value={data.contribution} onChange={(event) => updateSection('reflection', 'contribution', event.target.value)} />
        </label>
        <label>
          <span>What was challenging?</span>
          <textarea value={data.challenge} onChange={(event) => updateSection('reflection', 'challenge', event.target.value)} />
        </label>
      </div>
      <div className="toggle-row">
        <label><input type="checkbox" checked={data.teamIssues} onChange={(event) => updateSection('reflection', 'teamIssues', event.target.checked)} /> Team issue needs teacher attention</label>
      </div>
      <label className="form-section">
        <span>Team explanation</span>
        <textarea value={data.teamExplanation} onChange={(event) => updateSection('reflection', 'teamExplanation', event.target.value)} />
      </label>
    </Panel>
  );
}

function StudentGuide({ student }) {
  const chapter = PERIOD_CHAPTER_ASSIGNMENTS[String(student.period)] || {};

  return (
    <div className="student-view-stack">
      <HeroBand
        eyebrow="Guide"
        title="Keep the project brief in the portal."
        body="Students should not need a separate document to remember what the class period owns."
      />
      <div className="two-column">
        <Panel title={`Period ${student.period} segment`} subtitle={chapter.chapter || 'Living World segment'}>
          <p className="body-copy">{chapter.chapterTitle || chapter.careerLane || 'Career documentary production lane.'}</p>
          <div className="info-line"><span>Career</span><strong>{student.assignedCareer || student.career || 'Pending'}</strong></div>
          <div className="info-line"><span>Project</span><strong>{MEGA_PROJECT_TITLE}</strong></div>
        </Panel>
        <Panel title="Role expectations" subtitle="The live version should translate the rubric into plain next steps.">
          <div className="recommendation-list">
            {getRoles(student).map((role) => (
              <div className="recommendation" key={role}>
                <span>{role.slice(0, 2).toUpperCase()}</span>
                <p>{roleGuidance(role)}</p>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function StudentReflection({ student }) {
  return (
    <div className="student-view-stack">
      <HeroBand
        eyebrow="Reflection"
        title="Make evaluation feel like a closeout, not a hidden final form."
        body="The student version should summarize work completed, teammate collaboration, and what is still missing before students submit evaluations."
      />
      <Panel title="Closeout checklist" subtitle={`${student.alias || 'Student'} - Period ${student.period}`}>
        <div className="quality-list">
          <CheckLine text="I know my role in the final documentary." />
          <CheckLine text="My team has evidence for our career segment." />
          <CheckLine text="Our script and visuals match the science standard." />
          <CheckLine text="I can explain what I personally contributed." />
        </div>
      </Panel>
    </div>
  );
}

function recommendedModules(roles) {
  const normalized = roles.join(' ').toLowerCase();
  if (normalized.includes('research')) return MODULES.filter((m) => ['mission', 'brainstorm', 'research'].includes(m.id));
  if (normalized.includes('script')) return MODULES.filter((m) => ['mission', 'script', 'reflection'].includes(m.id));
  if (normalized.includes('visual') || normalized.includes('designer')) return MODULES.filter((m) => ['mission', 'media', 'reflection'].includes(m.id));
  if (normalized.includes('screen') || normalized.includes('talent')) return MODULES.filter((m) => ['mission', 'performance', 'reflection'].includes(m.id));
  return MODULES.slice(0, 3);
}

function hasLeadershipRole(student) {
  return getLeadershipTracks(student).length > 0;
}

function getLeadershipTracks(student) {
  const alias = student?.alias || '';
  const tracks = [];
  if (student?.boardMember || BOARD_MEMBER_ALIASES.includes(alias)) {
    tracks.push({ id: 'board', label: 'SAP Board', scope: 'Project-wide' });
  }
  if (student?.podLeader) {
    tracks.push({ id: 'pod', label: 'Pod Leader', scope: `Period ${student.period || '-'}` });
  }
  if (student?.careerLeader || student?.careerLed) {
    tracks.push({ id: 'career', label: 'Career Leader', scope: student.careerLed || student.assignedCareer || student.career || 'Career lane' });
  }
  return tracks;
}

function getRoles(student) {
  if (Array.isArray(student?.roles) && student.roles.length) return student.roles;
  if (student?.role) return [student.role === 'Writer' ? 'Scriptwriter' : student.role];
  return MEGA_ROLE_TAXONOMY.slice(0, 1);
}

function roleGuidance(role) {
  const map = {
    'Lead Researcher': 'Own the facts, vocabulary, and examples the team will trust.',
    Scriptwriter: 'Turn the research into narration that sounds clear when spoken.',
    'Visual Designer': 'Make the science visible through slides, media, and layout choices.',
    'On-Screen Talent': 'Practice delivery so the explanation is confident and understandable.',
    'Producer / Continuity': 'Keep the team organized and make sure every part connects.',
    'Fact-Checker / TEKS Verifier': 'Check that claims are accurate and tied to the expected science.',
  };
  return map[role] || 'Contribute clearly and help your team finish the segment.';
}

function StudentLoading() {
  return (
    <div className="lab-loading">
      <div className="loading-orbit" />
      <p>Preparing student workspace</p>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, helper }) {
  return (
    <article className="metric-card">
      <div className="metric-icon"><Icon size={20} /></div>
      <span>{label}</span>
      <strong>{value}</strong>
      {helper && <p>{helper}</p>}
    </article>
  );
}

function HeroBand({ eyebrow, title, body }) {
  return (
    <section className="hero-band">
      <span>{eyebrow}</span>
      <h1>{title}</h1>
      <p>{body}</p>
    </section>
  );
}

function Panel({ title, subtitle, children }) {
  return (
    <section className="lab-panel">
      <div className="panel-heading">
        <div>
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function CheckLine({ text }) {
  return (
    <div className="check-line">
      <CheckCircle2 size={18} />
      <span>{text}</span>
    </div>
  );
}

function ProgressMeter({ label, value, compact = false }) {
  const safeValue = Math.max(0, Math.min(100, Math.round(value || 0)));
  return (
    <div className={`progress-meter ${compact ? 'compact' : ''}`}>
      <div>
        <span>{label}</span>
        <strong>{safeValue}%</strong>
      </div>
      <div className="progress-track" aria-label={`${label} ${safeValue}%`}>
        <span style={{ width: `${safeValue}%` }} />
      </div>
    </div>
  );
}

export default StudentApp;
